export const CANONICAL_TOPICS = [
  'AI',
  'SaaS',
  'Fundraising',
  'M&A',
  'IPO',
  'Infrastructure',
  'Semiconductors',
  'Open Source',
  'Regulation',
  'Security',
  'Crypto',
  'Robotics',
  'Defence Tech',
  'Fintech',
  'Climate Tech',
  'Enterprise',
  'Developer Tools',
  'Biotech',
  'Hardware',
  'Consumer Tech',
] as const;

export type CanonicalTopic = (typeof CANONICAL_TOPICS)[number];
export type EntityKind = 'company' | 'ticker';
export type FeedPlacementSection = 'news' | 'research' | 'newsletters' | 'podcasts';

const COMPANY_SUFFIX_PATTERN =
  /\b(inc|incorporated|corp|corporation|co|company|ltd|limited|llc|plc|gmbh|holdings|holding)\b\.?$/i;

const TRACKING_PARAMS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'ref',
  'fbclid',
  'gclid',
]);

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function cleanTicker(value: string): string {
  return value.replace(/[^A-Za-z0-9]+/g, '').toUpperCase();
}

function normalizeCompanyName(rawValue: string): string {
  let normalized = rawValue
    .replace(/^@/, '')
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  while (COMPANY_SUFFIX_PATTERN.test(normalized)) {
    normalized = normalized.replace(COMPANY_SUFFIX_PATTERN, '').trim();
  }

  return normalized;
}

export function normalizeEntityAliasKey(rawInput: string): string | null {
  const trimmed = rawInput.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('$') || /^[A-Z]{2,5}$/.test(trimmed)) {
    const ticker = cleanTicker(trimmed.replace(/^\$/, ''));
    return ticker ? `ticker:${ticker}` : null;
  }

  const normalizedCompany = normalizeCompanyName(trimmed);
  const key = slugify(normalizedCompany);
  return key ? `company:${key}` : null;
}

export function buildEntityInputValue(entity: {
  name: string;
  kind: string;
  ticker?: string | null;
}): string {
  if (entity.kind === 'ticker') {
    return `$${entity.ticker ?? entity.name}`;
  }
  return entity.name;
}

export function normalizeFeedUrl(rawUrl: string): string | null {
  try {
    const parsed = new URL(rawUrl.trim());
    if (!/^https?:$/.test(parsed.protocol)) return null;

    parsed.hostname = parsed.hostname.toLowerCase().replace(/^www\./, '');
    parsed.hash = '';

    for (const key of [...parsed.searchParams.keys()]) {
      if (TRACKING_PARAMS.has(key.toLowerCase())) {
        parsed.searchParams.delete(key);
      }
    }

    parsed.searchParams.sort();

    let normalized = parsed.toString();
    if (normalized.endsWith('/') && parsed.pathname !== '/') {
      normalized = normalized.slice(0, -1);
    }

    return normalized;
  } catch {
    return null;
  }
}

function normalizeFeedHostname(rawUrl: string): string | null {
  try {
    return new URL(rawUrl.trim()).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
}

export function isSubstackFeedUrl(rawUrl: string): boolean {
  const hostname = normalizeFeedHostname(rawUrl);
  return (
    hostname === 'substack.com' ||
    hostname === 'api.substack.com' ||
    Boolean(hostname?.endsWith('.substack.com'))
  );
}

export function getSubstackSectionOverride(rawUrl: string): FeedPlacementSection | null {
  if (!isSubstackFeedUrl(rawUrl)) return null;

  try {
    const pathname = new URL(rawUrl.trim()).pathname.toLowerCase();
    if (pathname.includes('/feed/podcast/')) {
      return 'podcasts';
    }
  } catch {
    return null;
  }

  return 'newsletters';
}

export function deriveFeedNameFromUrl(rawUrl: string): string {
  try {
    const parsed = new URL(rawUrl);
    const host = parsed.hostname.replace(/^www\./, '');
    const [label = ''] = host.split('.');
    return label
      .split(/[-_]/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  } catch {
    return 'Custom Feed';
  }
}

export function normalizeEntityInput(rawInput: string): {
  canonicalKey: string;
  name: string;
  kind: EntityKind;
  ticker: string | null;
} | null {
  const trimmed = rawInput.trim();
  if (!trimmed) return null;

  const aliasKey = normalizeEntityAliasKey(trimmed);
  if (!aliasKey) return null;

  if (aliasKey.startsWith('ticker:')) {
    const ticker = aliasKey.replace('ticker:', '');
    return {
      canonicalKey: aliasKey,
      name: ticker,
      kind: 'ticker',
      ticker,
    };
  }

  const name = normalizeCompanyName(trimmed);
  if (!name) return null;

  return {
    canonicalKey: aliasKey,
    name,
    kind: 'company',
    ticker: null,
  };
}

export function formatEntityLabel(entity: {
  name: string;
  kind: string;
  ticker?: string | null;
}): string {
  if (entity.kind === 'ticker') {
    return `$${entity.ticker ?? entity.name}`;
  }
  return entity.name;
}
